import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { useMountEffect } from "primereact/hooks";
import { useIntl } from "react-intl";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";
import {
  userDetailsRequest,
  setDashboard,
} from "../../../store/slices/selfSlice";
import {
  learnerConfigurationRequest,
  languageConfigurationRequest,
} from "../../../store/slices/globalSlice";
import Header from "./header";
import LoadingBar from "./loadingBar";
import SideBar from "./sidebar";
import { MenuConstants, RouteConstants } from "../../../constants";
import TokenHandler from "./TokenHandler";
import NavigationUpdater from "./NavigationUpdater";
import { ActionNotAllowed } from "../pages";
import { changeNavigation } from "../../../store/slices/navigationSlice";
import * as GlobalHelper from "../../../helpers/GlobalHelper";

import MobileSideBar from "./sidebar/MobileSideBar";
import { TabPanel, TabView } from "primereact/tabview";
import useMobileView from "../../common/hooks/useMobileView";

const Home = ({
  dashboard,
  enterprise,
  isMenuCollapsed,
  setDashboard,
  user,
  userDetailsRequest,
  learnerConfigurationRequest,
  languageConfigurationRequest,
  requesting,
  navigation,
  changeNavigation,
  parentMenu,
  childMenu,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { isMobile } = useMobileView();

  const domain = window.location.hostname.split(".")[0];

  const isRootDomain = useMemo(
    () => GlobalHelper.CheckIfRootDomain(domain),
    [domain]
  );

  const [isHomeReady, setIsHomeReady] = React.useState(
    !isRootDomain ? (enterprise ? false : true) : true
  );

  useMountEffect(() => {
    userDetailsRequest();
    if (enterprise) {
      learnerConfigurationRequest();
      languageConfigurationRequest();
    }
  });

  useEffect(() => {
    if (!requesting && enterprise?.learningConfiguration) {
      setIsHomeReady(true);
    }
  }, [requesting, enterprise]);

  useEffect(() => {
    if (!dashboard) {
      if (enterprise) {
        setDashboard("learner");
      } else {
        setDashboard("admin");
      }
    }
  }, [enterprise, setDashboard, dashboard]);

  const getAccessibleMenus = () => {
    const { role } = user || {};

    let menus = MenuConstants.ADMIN_MENU_ITEMS;
    if (enterprise && enterprise?.logo && dashboard === "admin") {
      menus = MenuConstants.ENTERPRISE_ADMIN_MENU_ITEMS.concat(
        MenuConstants.PROFILE_MENU
      ).concat(MenuConstants.ACCOUNT_PREFERENCES_MENU);
    } else if (enterprise && enterprise?.logo && dashboard === "learner") {
      const { learnerSettings = {} } = enterprise?.learningConfiguration || {};
      const { customLabels, defaultDashboard } = learnerSettings;
      menus = MenuConstants.LEARNER_MENU_ITEMS;
      menus = menus
        .map((menu) => {
          if (menu.id === MenuConstants.LEARNING_JOURNEY) {
            menu.label = customLabels?.learningJourney || menu.label;
            if (customLabels?.learningJourney) {
              menu.customised = true;
            }
            if (defaultDashboard === MenuConstants.LEARNING_JOURNEY) {
              menu.index = true;
            } else {
              menu.index = false;
            }
          } else if (menu.id === MenuConstants.DASHBOARD) {
            menu.label = customLabels?.dashboard || menu.label;
            if (customLabels?.dashboard) {
              menu.customised = true;
            }

            if (
              !defaultDashboard ||
              defaultDashboard === MenuConstants.DASHBOARD
            ) {
              menu.index = true;
            } else {
              menu.index = false;
            }
          }
          return menu;
        })
        .concat(MenuConstants.PROFILE_MENU)
        .concat(MenuConstants.ACCOUNT_PREFERENCES_MENU);
    }

    return menus.filter((item) => {
      if (item.Component || item.childrens) {
        if (item.scope) {
          return item.scope.filter((scope) => role?.scope?.[scope]).length > 0;
        }
        if (item.conditions) {
          const isAllowed = Object.keys(item.conditions).reduce(
            (acc, condition) => {
              return user?.[condition] === item.conditions[condition] && acc;
            },
            true
          );
          return isAllowed;
        }
        return true;
      }
      return false;
    });
  };

  let menus = MenuConstants.ADMIN_MENU_ITEMS;
  if (enterprise && dashboard === "admin") {
    menus = MenuConstants.ENTERPRISE_ADMIN_MENU_ITEMS;
  } else if (enterprise && dashboard === "learner") {
    menus = MenuConstants.LEARNER_MENU_ITEMS;
  }

  const selectedParent = menus.find((menu) => menu.id === parentMenu);
  const hasTertiaryMenu =
    selectedParent?.childrens?.filter((menu) => menu?.childrens?.length > 0)
      ?.length > 0;

  const handleChildMenu = (menu) => {
    if (menu.id === childMenu) {
      return;
    }
    changeNavigation({
      child: menu.id,
    });
    navigate(menu.to);
  };

  const getAccessibleChildrens = (childrens, selectedScopes) => {
    return childrens.filter((item) => {
      if (item.Component) {
        if (item.permission) {
          const isAllowed =
            selectedScopes.filter((scope) => scope?.includes(item.permission))
              .length > 0;

          if (!isAllowed) {
            item.Component = ActionNotAllowed;
          }
        }
        return true;
      }
      return true;
    });
  };

  const generateMenuRoutes = (menu, index, level = 0, parent = null) => {
    if (menu) {
      if (menu.childrens) {
        const selectedScopes = (menu.scope || parent?.scope || []).map(
          (scope) => user?.role?.scope?.[scope]
        );
        const accessibleChildrens = getAccessibleChildrens(
          menu.childrens,
          selectedScopes
        );
        const indexChild =
          accessibleChildrens.find((child) => child.index) ||
          accessibleChildrens[0];
        const childRoutes = accessibleChildrens.map((child, childIndex) => {
          return (
            (child.childrens &&
              generateMenuRoutes(child, childIndex, level + 1, menu)) ||
            (child.Component && (
              <Route
                key={childIndex}
                path={child.to}
                element={
                  <NavigationUpdater
                    menu={menu}
                    parent={parent || menu}
                    shouldUpdate={true}
                    action={menu.id || ""}
                  >
                    <child.Component
                      parent={parent || menu}
                      menu={menu}
                      isEdit={child.id === MenuConstants.EDIT}
                      menuCustomised={menu.customised || false}
                      customisations={{
                        label: menu.label,
                      }}
                      additionalProps={{ ...child.additionalProps }}
                      labels={[
                        { label: parent?.label || "", to: parent?.to || "" },
                        { label: menu.label, to: menu.to, isParent: true },
                        { label: child.label || "" },
                      ]}
                    />
                  </NavigationUpdater>
                }
              ></Route>
            )) || <Route key={childIndex} path={child?.to} />
          );
        });
        return (
          <Route key={index} path={`${menu.to}`}>
            {childRoutes}
            <Route index element={<Navigate to={indexChild?.to} replace />} />
          </Route>
        );
      } else if (level !== 0) {
        return (
          <Route key={menu.to} path={menu.to} element={<menu.Component />} />
        );
      } else if (level === 0) {
        return (
          <Route
            key={index}
            path={`${menu.to}`}
            element={
              <NavigationUpdater
                shouldUpdate={true}
                action={menu?.id || ""}
                menu={menu}
                parent={parent || menu}
              />
            }
          >
            <Route
              index
              key={menu.to}
              path={menu.to}
              element={<menu.Component />}
            />
          </Route>
        );
      }
    }
    return null;
  };

  const renderRoutes = () => {
    const menus = getAccessibleMenus();
    const indexMenu = menus.find((menu) => menu.index) ?? menus[0];
    return (
      <Routes>
        {menus.map((menu, index) => {
          return generateMenuRoutes(menu, index);
        })}
        <Route
          index
          path="/*"
          element={<Navigate to={indexMenu.to} replace />}
        />
      </Routes>
    );
  };

  const checkIfLanguageNotSet = () => {
    return (
      (dashboard === "learner" &&
        navigation?.parent !== "ACCOUNT_PREFERENCES" &&
        ((enterprise?.languageConfiguration?.allowedLanguages.length > 1 &&
          !user.preferredLanguage) ||
          (user.preferredLanguage &&
            !enterprise?.languageConfiguration?.allowedLanguages.includes(
              user.preferredLanguage
            ))) &&
        true) ||
      false
    );
  };

  return (
    <div className="flex flex-column">
      {(isHomeReady && (user?.id || user?.details?.id) && (
        <>
          <TokenHandler />
          <Header />
          <LoadingBar />
          <div className="flex md:flex flex-1 edexlms-main-container relative">
            <SideBar />
            <MobileSideBar />

            <div
              className={`edexlms-main-content ${dashboard} ${
                isMenuCollapsed ? "close" : "open"
              } ${
                checkIfLanguageNotSet() ? "language-warning" : ""
              } flex-1 overflow-scroll ${isMobile ? "mobile" : ""}`}
            >
              {(checkIfLanguageNotSet() && (
                <div className="edexlms-language-warning flex justify-content-center align-items-center">
                  <Message
                    severity="error"
                    text={
                      <span>
                        <span
                          className="edexlms-link"
                          onClick={() => {
                            navigate(RouteConstants.APP_ROUTE_PREFERENCES);
                            changeNavigation({
                              parent: "ACCOUNT_PREFERENCES",
                            });
                          }}
                        >
                          Click here
                        </span>{" "}
                        to update your preferred language for receiving
                        notifications. Current default - English.
                      </span>
                    }
                  ></Message>
                </div>
              )) ||
                null}
              {renderRoutes()}
              {hasTertiaryMenu ? (
                <footer
                  className={`border-top-1 border-200 absolute bottom-0 w-full black md:hidden z-5 bg-white`}
                >
                  <TabView className="w-full">
                    {selectedParent.childrens.map((menu) => {
                      return (
                        <TabPanel
                          key={menu.id}
                          className={`flex-1 ${
                            menu.id === childMenu
                              ? "text-primary font-bold"
                              : ""
                          } border-x-1 border-200 cursor-pointer`}
                          headerTemplate={() => (
                            <p
                              className="text-center py-2"
                              onClick={() => handleChildMenu(menu)}
                            >
                              <span>
                                {intl.formatMessage({ id: menu.label })}
                              </span>
                            </p>
                          )}
                        />
                      );
                    })}
                  </TabView>
                </footer>
              ) : null}
            </div>
          </div>
        </>
      )) || (
        <div className="edexlms-spinner">
          <ProgressSpinner />
          <h2>
            {intl.formatMessage({
              id: "loading_enterprise_config",
              defaultMessage: "Loading Configuration. Please wait...",
            })}
          </h2>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  isMenuCollapsed: state?.navigation?.isMenuCollapsed,
  user: state?.self?.user,
  dashboard: state?.self?.dashboard,
  enterprise: state?.global?.enterprise,
  isRoleSwitching: state?.self?.isRoleSwitching,
  requesting: state?.global?.requesting,
  navigation: state?.navigation,
  isSidebarOpen: state?.navigation?.isSideBarOpen,
  parentMenu: state?.navigation?.parent,
  childMenu: state?.navigation?.child,
});

const mapDispatchToProps = (dispatch) => ({
  userDetailsRequest: () => dispatch(userDetailsRequest()),
  setDashboard: (data) => dispatch(setDashboard(data)),
  learnerConfigurationRequest: (data) =>
    dispatch(learnerConfigurationRequest(data)),
  languageConfigurationRequest: (data) =>
    dispatch(languageConfigurationRequest(data)),
  changeNavigation: (data) => dispatch(changeNavigation(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Home);
